/*
 * The client for the backend in ../server.
 *
 * Plain HTTP and JSON. The session is a cookie the browser holds and script cannot read, so there
 * is no token to keep here, nothing to put in localStorage, and nothing to refresh — a 401 simply
 * means the session is over.
 *
 * Every method here maps onto one endpoint. The mapping is dull on purpose: the interesting
 * decisions were all made on the server side, where the mail actually is.
 */

import {
    type Email,
    type EmailAddress,
    type FolderStatus,
    type Identity,
    type Mailbox,
    MailError,
    type SearchResults,
    type SendResult,
    packId,
    unpackId,
    type Contact,
    type ContactBook,
    type Calendar,
    type CalendarEvent,
    type Forwarding,
    type Vacation,
    type RulesState,
    type AdminMailbox,
    type AdminSharedState,
} from './types';

/*
    Who to tell when a mailbox's session turns out to be over.

    Sessions live only in the server's memory, so every deploy ends all of them while the tabs
    stay open. Before this, each screen met the 401 on its own and showed it as an error — the
    list empty, the folders missing — and the way out was to find "sign out" and sign in again
    (reported 2026-10-12, after the 2026-10-12a deploy). One listener, set by the accounts store,
    turns the first 401 into asking the server which sessions are still open.
*/
let sessionEnded: ((mailbox: string) => void) | null = null;
export function onSessionEnded(listener: ((mailbox: string) => void) | null): void {
    sessionEnded = listener;
}

/** What the backend sends for a folder. */
interface FolderJSON {
    name: string;
    display: string;
    parent: string;
    depth: number;
    role?: string;
    selectable: boolean;
    subscribed: boolean;
    total: number;
    unseen: number;
}

/** What the backend sends for a message in a listing. */
interface SummaryJSON {
    uid: number;
    subject: string;
    from: { name?: string; email: string }[] | null;
    to: { name?: string; email: string }[] | null;
    date: string;
    size: number;
    seen: boolean;
    flagged: boolean;
    answered: boolean;
    draft: boolean;
    hasAttachment: boolean;
    threadCount?: number;
    threadUnread?: number;
    folder?: string;
    uidValidity?: number;
}

interface MessageJSON extends SummaryJSON {
    cc?: { name?: string; email: string }[];
    bcc?: { name?: string; email: string }[];
    replyTo?: { name?: string; email: string }[];
    messageId?: string;
    inReplyTo?: string[];
    references?: string[];
    listUnsubscribe?: string;
    authResults?: string;
    text: string;
    html: string;
    truncated: boolean;
    attachments: PartJSON[];
    inline: PartJSON[];
}

interface PartJSON {
    section: string;
    mediaType: string;
    filename?: string;
    size: number;
    contentId?: string;
}

/** The order folders appear in when the server has not labelled them. */
const ROLE_ORDER: Record<string, number> = {
    inbox: 0,
    drafts: 1,
    sent: 2,
    archive: 3,
    junk: 4,
    trash: 5,
};

/** What the server answers about the signed-in session — including what this deployment can do. */
export interface Identity0 {
    address: string;
    /** Whether office attachments can be shown rather than only saved. False on a server with no converter. */
    canPreviewDocuments?: boolean;
    /**
     * Whether this deployment has a CalDAV/CardDAV server behind it — contacts AND calendar, which
     * is why it is named for the server. False leaves both screens out entirely.
     */
    hasDAV?: boolean;
    /**
     * Whether this deployment has a ManageSieve server behind it, which is what makes forwarding
     * and the automatic reply possible. Its own flag rather than a second meaning on
     * hasDAV: they are different servers and either can be absent on its own.
     */
    hasForwarding?: boolean;
    /**
     * Whether THIS mailbox administers a domain. A property of the mailbox, not of the
     * deployment — the server settles it at sign-in and it cannot change while a session lasts.
     */
    hasAdmin?: boolean;
}

/**
 * One folder's worth of a move, and the way back.
 *
 * `ids` are where the messages are NOW — the destination issued new UIDs, and these are the only
 * handles that still refer to them. Empty means the server did not report them, which makes the
 * move final as far as the interface is concerned.
 */
export interface MoveStep {
    from: string;
    to: string;
    ids: string[];
}

export class MailClient {
    /**
     * base: where the backend is (same origin in production, proxied by vite in development).
     * mailbox: which of the mailboxes signed in on this browser this client speaks for. Each mailbox
     * has its own session cookie; the server picks it by the X-Webmail-Mailbox header (or ?acct= on
     * the URLs the browser fetches by itself). Without it the server uses the only open session and
     * refuses when there are several — so a client with no mailbox is only for signing in.
     */
    constructor(
        private base = '/api',
        readonly mailbox?: string,
    ) {}

    /** Adds the mailbox selector to a URL the browser will fetch by itself (no header possible). */
    private secili(url: string): string {
        if (!this.mailbox) return url;
        return `${url}${url.includes('?') ? '&' : '?'}acct=${encodeURIComponent(this.mailbox)}`;
    }

    // ---- the session ----

    /** remember asks for the long session (a week idle, a month at most) instead of the day-long one. */
    async signIn(address: string, password: string, remember = false): Promise<Identity0> {
        return this.post('/session', { address, password, remember });
    }

    async whoami(): Promise<Identity0 | null> {
        try {
            return await this.get('/session');
        } catch (err) {
            if (err instanceof MailError && err.status === 401) return null;
            throw err;
        }
    }

    async signOut(): Promise<void> {
        await this.send('DELETE', '/session');
    }

    /** The mailboxes signed in on this browser, as the server sees their cookies. */
    async sessions(): Promise<{ sessions: { address: string; hasAdmin: boolean }[]; canPreviewDocuments?: boolean; hasDAV?: boolean; hasForwarding?: boolean }> {
        return this.get('/sessions');
    }

    // ---- folders ----

    async mailboxes(_accountId?: string): Promise<Mailbox[]> {
        const body = await this.get<{ folders: FolderJSON[] }>('/folders');
        return (body.folders ?? []).map((f) => ({
            id: f.name,
            name: f.display || f.name,
            parentId: f.parent || null,
            role: f.role ?? null,
            sortOrder: f.role ? (ROLE_ORDER[f.role] ?? 90) : 99,
            totalEmails: f.total,
            unreadEmails: f.unseen,
            isSubscribed: f.subscribed,
            selectable: f.selectable,
            depth: f.depth,
        }));
    }

    async status(folders: string[]): Promise<FolderStatus[]> {
        if (folders.length === 0) return [];
        const body = await this.get<{ status: FolderStatus[] }>(
            `/status?folders=${folders.map(encodeURIComponent).join(',')}`,
        );
        return body.status ?? [];
    }

    // ---- reading ----

    async listEmails(
        folder: string,
        offset = 0,
        limit = 50,
        sort?: { field: string; ascending: boolean },
        threads = false,
    ): Promise<{ emails: Email[]; total: number; uidValidity: number }> {
        const params = new URLSearchParams({ folder, offset: String(offset), limit: String(limit) });
        // Asked for, never assumed. Grouping costs the server a THREAD over the whole folder,
        // which the flat listing does not, so a caller that says nothing gets the cheap one.
        if (threads) params.set('threads', 'true');
        // Only when it is not the default: any other order costs the server a full sort of the
        // mailbox, and the backend has a much cheaper path for newest-first.
        if (sort && (sort.ascending || (sort.field && sort.field !== 'receivedAt'))) {
            params.set('sort', sort.field);
            if (sort.ascending) params.set('asc', 'true');
        }
        const body = await this.get<{
            folder: string;
            uidValidity: number;
            total: number;
            offset: number;
            messages: SummaryJSON[];
        }>(`/messages?${params.toString()}`);

        return {
            emails: (body.messages ?? []).map((m) => summaryToEmail(m, body.folder, body.uidValidity)),
            total: body.total,
            uidValidity: body.uidValidity,
        };
    }

    async getEmail(id: string): Promise<Email | null> {
        const { folder, uid } = unpackId(id);
        if (!folder || !uid) return null;
        try {
            const m = await this.get<MessageJSON>(
                `/message?folder=${encodeURIComponent(folder)}&uid=${uid}`,
            );
            return messageToEmail(m, folder);
        } catch (err) {
            if (err instanceof MailError && err.status === 404) return null;
            throw err;
        }
    }

    /**
     * The URL for one attachment.
     *
     * `inline` asks the server to serve it for display rather than download, which it only agrees
     * to for a short list of types it considers safe — see the server's attachment.go. Asking is
     * not the same as getting, which is the point.
     */
    attachmentUrl(id: string, section: string, inline = false): string {
        const { folder, uid } = unpackId(id);
        const query = new URLSearchParams({ folder, uid: String(uid), part: section });
        if (inline) query.set('disposition', 'inline');
        return this.secili(`${this.base}/attachment?${query.toString()}`);
    }

    /**
     * The URL for the whole message as a .eml file.
     *
     * A link rather than a fetch: the browser streams it to disk, so a large message never sits
     * in the page's memory on the way past.
     */
    rawUrl(id: string, name: string): string {
        const { folder, uid } = unpackId(id);
        const query = new URLSearchParams({ folder, uid: String(uid), name });
        return this.secili(`${this.base}/message/raw?${query.toString()}`);
    }

    /**
     * The URL for an office document, converted to PDF so it can be looked at.
     *
     * Only the server knows whether a converter is configured, which is why the session says so
     * rather than this guessing — see canPreviewDocuments.
     */
    previewUrl(id: string, section: string): string {
        const { folder, uid } = unpackId(id);
        const query = new URLSearchParams({ folder, uid: String(uid), part: section });
        return this.secili(`${this.base}/preview?${query.toString()}`);
    }

    async downloadAttachment(id: string, section: string): Promise<Blob> {
        const res = await fetch(this.attachmentUrl(id, section), { credentials: 'same-origin' });
        if (!res.ok) throw new MailError('that attachment could not be downloaded', res.status);
        return res.blob();
    }

    // ---- changing things ----

    async setFlags(ids: string[], add: string[], remove: string[]): Promise<void> {
        for (const [folder, group] of groupByFolder(ids)) {
            await this.post('/messages/flags', {
                folder,
                uids: group.uids,
                uidValidity: group.uidValidity,
                add,
                remove,
            });
        }
    }

    /**
     * Moves messages, and says how to move them back.
     *
     * A moved message is issued a new UID by the folder it lands in, so the id the caller was
     * holding no longer points at anything. Undo needs the new ones; the server reports them and
     * this hands them back as ids, ready to move again.
     *
     * A step with no ids is a server that did not say where they went — the move still worked,
     * the way back is simply unknown, and the interface should not offer to undo it.
     */
    async move(ids: string[], to: string): Promise<MoveStep[]> {
        const steps: MoveStep[] = [];
        for (const [folder, group] of groupByFolder(ids)) {
            if (folder === to) continue;
            const result = await this.post<{ uidValidity?: number; uids?: number[] }>('/messages/move', {
                folder,
                uids: group.uids,
                uidValidity: group.uidValidity,
                to,
            });
            steps.push({
                from: folder,
                to,
                ids: (result.uids ?? []).map((uid) => packId(to, uid)),
            });
        }
        return steps;
    }

    /** Moves to Trash, or erases if they are already in it. The server decides which. */
    async remove(ids: string[]): Promise<{ erased: boolean; to?: string; steps: MoveStep[] }> {
        let erased = false;
        let to: string | undefined;
        const steps: MoveStep[] = [];
        for (const [folder, group] of groupByFolder(ids)) {
            const result = await this.post<{
                erased: boolean;
                to?: string;
                uidValidity?: number;
                uids?: number[];
            }>('/messages/delete', {
                folder,
                uids: group.uids,
                uidValidity: group.uidValidity,
            });
            erased = erased || result.erased;
            to = to ?? result.to;
            // Erased mail has nowhere to come back from, so it contributes no way back.
            if (!result.erased && result.to) {
                steps.push({
                    from: folder,
                    to: result.to,
                    ids: (result.uids ?? []).map((uid) => packId(result.to!, uid)),
                });
            }
        }
        return { erased, to, steps };
    }

    // ---- the address book ----

    /*
        Contacts, or nothing.

        A 404 here is the supported answer, not a failure: an installation with no DAV server has
        no address book, and the interface offers no address book screen. Anything else is a real
        error and is thrown.
    */
    async contacts(): Promise<{ books: ContactBook[]; contacts: Contact[] } | null> {
        try {
            return await this.get<{ books: ContactBook[]; contacts: Contact[] }>('/contacts');
        } catch (err) {
            if (err instanceof MailError && err.status === 404) return null;
            throw err;
        }
    }

    async saveContact(contact: Partial<Contact> & { book?: string }): Promise<Contact> {
        return this.post<Contact>('/contacts', contact);
    }

    /*
        Removing a contact hands back the card that was removed.

        It is kept only for as long as the undo offer stands, and it is what goes back if somebody
        takes the deletion back — the card itself, not the five fields this app models. A contact
        synced from a phone carries a photograph, and an undo that returned somebody without theirs
        would be a loss nobody would think to look for.

        An empty card means the server could not hold it (too large), and then no undo is offered.
    */
    async deleteContact(id: string): Promise<{ id: string; card: string }> {
        return this.del<{ id: string; card: string }>(`/contacts?id=${encodeURIComponent(id)}`);
    }

    async restoreContact(id: string, card: string): Promise<void> {
        await this.post<void>('/contacts/restore', { id, card });
    }

    // ---- forwarding and the automatic reply ----

    /*
        What the mailbox's filter script says, or nothing.

        A 404 is the supported answer, exactly as with the address book: an installation with no
        ManageSieve server has no forwarding, and the interface offers no forwarding screen.

        Both halves come back from every call, because they live in one script on the server and a
        screen that showed one of them stale would be showing somebody settings they do not have.
    */
    async rules(): Promise<RulesState | null> {
        try {
            return await this.get<RulesState>('/rules');
        } catch (err) {
            if (err instanceof MailError && err.status === 404) return null;
            throw err;
        }
    }

    /**
     * The management endpoints.
     *
     * Nothing privileged travels from the browser: the server carries the signed-in person's own
     * mailbox password to the management service, which checks it against the mail server. These
     * answer 404 when no service is configured and 403 when this mailbox administers nothing —
     * and the screen never calls them in either case, because the tab is not drawn.
     */
    async adminMailboxes(): Promise<AdminMailbox[]> {
        const answer = await this.get<{ mailboxes: AdminMailbox[] }>('/admin/mailboxes');
        return answer.mailboxes ?? [];
    }

    async adminCreateMailbox(address: string, name: string, password: string): Promise<void> {
        await this.post('/admin/mailboxes', { address, name, password });
    }

    /**
     * Resets a mailbox's password.
     *
     * Answers whether this ended the session. Resetting your OWN password does: the session holds
     * the password in memory for every IMAP and SMTP connection, and after the reset that copy is
     * the old one. The server ends the session rather than letting the next request fail — the
     * screen used to show a green "password changed" beside a red failure from the same click.
     */
    async adminResetPassword(address: string, password: string): Promise<{ signedOut: boolean }> {
        const answer = await this.post<{ ok: boolean; signedOut?: boolean }>('/admin/password', { address, password });
        return { signedOut: answer.signedOut === true };
    }

    /**
     * Opens the domain's shared address book and calendar.
     *
     * Unlike the two above this never reaches the management service: the DAV rights rule already
     * gives everyone in a domain read and write there, so the person's own password is enough.
     * Running it twice is not an error — the answer says, for each collection, whether it was made
     * or was already there.
     */
    async adminOpenShared(): Promise<AdminSharedState> {
        return this.post<AdminSharedState>('/admin/shared', {});
    }

    /**
     * Puts the operator's logo up, or takes it down.
     *
     * The file goes as raw bytes rather than as a form: the management service on the other side
     * reads the type out of the file itself and ignores what anybody claims it is, so a multipart
     * envelope would only add a filename nobody reads.
     *
     * Which domain is NOT sent. The server takes it from the session's own authority, because the
     * browser is not the thing that decides what somebody administers.
     */
    async adminSaveLogo(file: Blob, tema: 'acik' | 'koyu' = 'acik'): Promise<{ domain: string }> {
        const q = tema === 'koyu' ? '?tema=koyu' : '';
        return this.send<{ domain: string }>('POST', `/admin/logo${q}`, { body: file });
    }

    /** Removing the light logo removes the dark one with it: a dark mark alone is never drawn. */
    async adminRemoveLogo(tema: 'acik' | 'koyu' = 'acik'): Promise<void> {
        const q = tema === 'koyu' ? '?tema=koyu' : '';
        await this.send<{ ok: boolean }>('DELETE', `/admin/logo${q}`);
    }

    async saveForwarding(forwarding: Forwarding): Promise<RulesState> {
        return this.post<RulesState>('/forwarding', forwarding);
    }

    async saveVacation(vacation: Vacation): Promise<RulesState> {
        return this.post<RulesState>('/vacation', vacation);
    }

    /** Turns both off and takes the script away, rather than leaving one that says to do nothing. */
    async clearRules(): Promise<RulesState> {
        return this.del<RulesState>('/rules');
    }

    // ---- the calendar ----

    /*
        A month, or nothing.

        The range is asked for as instants and covers the whole grid, neighbouring days included.
        A 404 is the supported answer for an installation with no DAV server, exactly as with the
        address book: there is no calendar screen there at all.
    */
    async events(from: string, to: string): Promise<{ calendars: Calendar[]; events: CalendarEvent[] } | null> {
        const query = `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
        try {
            return await this.get<{ calendars: Calendar[]; events: CalendarEvent[] }>(`/events?${query}`);
        } catch (err) {
            if (err instanceof MailError && err.status === 404) return null;
            throw err;
        }
    }

    async saveEvent(event: Partial<CalendarEvent> & { calendar?: string }): Promise<CalendarEvent> {
        return this.post<CalendarEvent>('/events', event);
    }

    /*
        Removing an event hands back the file that was removed — the alarm, the people invited, the
        repeat rule — so that undo puts THAT back rather than the handful of fields this app draws.
        An empty string means the server could not hold it, and then no undo is offered.
    */
    async deleteEvent(id: string): Promise<{ id: string; event: string }> {
        return this.del<{ id: string; event: string }>(`/events?id=${encodeURIComponent(id)}`);
    }

    async restoreEvent(id: string, event: string): Promise<void> {
        await this.post<void>('/events/restore', { id, event });
    }

    // ---- searching ----

    async search(query: {
        folder?: string;
        text?: string;
        from?: string;
        to?: string;
        subject?: string;
        /*
            Flags the message must, and must not, carry. Lists rather than a boolean each: the
            search box already offered is:read, is:draft and is:answered, and each of them was
            quietly discarded on the way here because there was no boolean to put it in.
        */
        flags?: string[];
        notFlags?: string[];
        hasAttachment?: boolean;
        since?: string;
        before?: string;
        offset?: number;
        limit?: number;
    }): Promise<SearchResults> {
        const params = new URLSearchParams();
        if (query.folder) params.set('folder', query.folder);
        if (query.text) params.set('q', query.text);
        if (query.from) params.set('from', query.from);
        if (query.to) params.set('to', query.to);
        if (query.subject) params.set('subject', query.subject);
        for (const flag of query.flags ?? []) params.append('flag', flag);
        for (const flag of query.notFlags ?? []) params.append('notFlag', flag);
        if (query.hasAttachment) params.set('hasAttachment', 'true');
        if (query.since) params.set('since', query.since);
        if (query.before) params.set('before', query.before);
        if (query.offset) params.set('offset', String(query.offset));
        if (query.limit) params.set('limit', String(query.limit));

        const body = await this.get<{
            matches: SummaryJSON[];
            offset: number;
            total: number;
            searched: string[];
            partial: boolean;
        }>(`/search?${params.toString()}`);

        return {
            matches: (body.matches ?? []).map((m) =>
                summaryToEmail(m, m.folder ?? query.folder ?? '', m.uidValidity ?? 0),
            ),
            offset: body.offset,
            total: body.total,
            searched: body.searched ?? [],
            partial: body.partial,
        };
    }

    // ---- writing ----

    async sendEmail(draft: OutgoingMessage): Promise<SendResult> {
        return this.postForm('/send', draft);
    }

    async saveDraft(draft: OutgoingMessage, replaces?: number): Promise<{ folder: string; uid: number; replaced?: number }> {
        return this.postForm('/draft', draft, replaces);
    }

    // ---- the one identity ----

    /**
     * There is exactly one identity: the mailbox that signed in. Their server has no notion of
     * send-as, and the backend refuses to send as anybody else, so offering a picker would be
     * offering something that cannot work.
     *
     * The name and signature now live IN THE ACCOUNT, as a message in a folder of its own —
     * `server/internal/mailbox/identity.go` says why that and not IMAP METADATA. They used to
     * live only here in the browser, which meant a second computer had no signature at all.
     *
     * localStorage is still used, but as a CACHE rather than the truth: the composer opens
     * instantly with the last known signature instead of waiting for a round trip, and a brief
     * outage shows the signature the person had rather than none.
     */
    async identity(address: string): Promise<Identity> {
        /*
            A failure is THROWN, not swallowed into an empty signature.

            The tempting version returned the cache on error, and it is wrong in the exact case
            this feature exists for: somebody signing in on a new computer has no cache, so a
            failed read would show them an empty editor. They would conclude they had never set a
            signature, type a new one, and save it OVER the real one. Silence there costs the
            thing being protected.

            The composer may still fall back to the cache — a reply going out without a signature
            is an annoyance, not a loss — and it does that itself, where the choice is visible.
        */
        const stored = await this.get<{ name: string; htmlSignature: string; textSignature: string }>('/identity');
        const cached = readStoredIdentity(address);
        const empty = !stored.name && !stored.htmlSignature && !stored.textSignature;
        /*
            Nothing in the account but something in this browser: that is somebody who set their
            signature before it could travel. It is carried up on the spot rather than left
            behind — a migration that waits for the person to notice and retype is not one.
        */
        if (empty && (cached.name || cached.htmlSignature || cached.textSignature)) {
            return this.saveIdentity(address, cached);
        }
        this.cacheIdentity(address, stored);
        return { id: address, email: address, ...stored };
    }

    /** What the composer can paint with before the server answers. Never the truth, only the last answer. */
    cachedIdentity(address: string): Identity | null {
        const cached = readStoredIdentity(address);
        if (!cached.name && !cached.htmlSignature && !cached.textSignature) return null;
        return { id: address, email: address, ...cached };
    }

    async saveIdentity(address: string, patch: Partial<Omit<Identity, 'id' | 'email'>>): Promise<Identity> {
        const next = { ...readStoredIdentity(address), ...patch };
        const stored = await this.post<{ name: string; htmlSignature: string; textSignature: string }>('/identity', next);
        this.cacheIdentity(address, stored);
        return { id: address, email: address, ...stored };
    }

    private cacheIdentity(address: string, value: { name: string; htmlSignature: string; textSignature: string }) {
        try {
            localStorage.setItem(identityKey(address), JSON.stringify(value));
        } catch {
            /* private mode or quota — the account still has it, this is only the cache */
        }
    }


    // ---- folder management ----

    async createMailbox(_accountId: string, args: { name: string; parentId?: string | null }): Promise<Mailbox> {
        // The backend takes a full path, because that is what IMAP has. A parent plus a leaf is
        // the interface's way of thinking about it, so the two are joined here.
        const name = args.parentId ? `${args.parentId}${this.delimiter}${args.name}` : args.name;
        const folder = await this.post<FolderJSON>('/folders', { name });
        return toMailbox(folder);
    }

    async updateMailbox(_accountId: string, id: string, patch: Record<string, unknown>): Promise<void> {
        const body: Record<string, unknown> = { name: id };
        if (typeof patch.name === 'string') {
            // A rename keeps the folder where it is: only the last segment changes.
            const at = id.lastIndexOf(this.delimiter);
            body.to = at >= 0 ? id.slice(0, at + 1) + patch.name : patch.name;
        }
        if (typeof patch.parentId === 'string' || patch.parentId === null) {
            const leaf = (body.to as string | undefined) ?? id;
            const name = leaf.slice(leaf.lastIndexOf(this.delimiter) + 1);
            body.to = patch.parentId ? `${patch.parentId}${this.delimiter}${name}` : name;
        }
        if (typeof patch.isSubscribed === 'boolean') body.subscribed = patch.isSubscribed;
        await this.send('PATCH', '/folders', {
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(body),
        });
    }

    async destroyMailbox(_accountId: string, id: string, _removeEmails = false): Promise<void> {
        await this.send('DELETE', '/folders', {
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ name: id }),
        });
    }

    /** Erases what is in the Trash or Junk folder; the folder stays. Says how many went. */
    async emptyMailbox(id: string): Promise<number> {
        const r = await this.post<{ count: number }>('/folders/empty', { name: id });
        return r.count ?? 0;
    }

    // ---- what the list layer asks for ----

    /**
     * Runs a search and returns the ids, in order.
     *
     * The messages themselves are kept in `seen` on the way past, so getEmailHeaders can answer
     * from there instead of asking again — the backend already sends everything a row needs, and
     * fetching the same page twice to fill in a list would be silly.
     *
     * A term IMAP cannot express is thrown as an unsupportedFilter error naming that term. The
     * list layer already knows what to do with that: drop it, say so on screen, and re-run the
     * rest. It was written for a JMAP server refusing a condition, and it fits this exactly.
     */
    async queryEmails(opts: {
        accountId?: string;
        filter: Record<string, unknown> | null;
        position: number;
        limit: number;
        collapseThreads?: boolean;
        sort?: { field: string; ascending: boolean };
    }): Promise<{ ids: string[]; position: number; total?: number }> {
        const plan = planQuery(opts.filter);

        if (plan.kind === 'list') {
            const page = await this.listEmails(
                plan.folder,
                opts.position,
                opts.limit,
                opts.sort,
                opts.collapseThreads,
            );
            for (const email of page.emails) this.seen.set(email.id, email);
            return { ids: page.emails.map((e) => e.id), position: opts.position, total: page.total };
        }

        const results = await this.search({
            ...plan.query,
            offset: opts.position,
            limit: opts.limit,
        });
        for (const email of results.matches) this.seen.set(email.id, email);
        return { ids: results.matches.map((e) => e.id), position: opts.position, total: results.total };
    }

    async getEmailHeaders(_accountId: string, ids: string[]): Promise<Email[]> {
        const out: Email[] = [];
        for (const id of ids) {
            const known = this.seen.get(id);
            if (known) {
                out.push(known);
                continue;
            }
            // Not in hand, so ask. This happens when a message is opened from a link rather
            // than from a list that has just been drawn.
            const email = await this.getEmail(id);
            if (email) {
                this.seen.set(id, email);
                out.push(email);
            }
        }
        return out;
    }

    async getEmailFull(_accountId: string, id: string): Promise<Email | null> {
        return this.getEmail(id);
    }

    /** One message's thread. There is no server-side threading here — see the note in Reader. */
    /**
     * The conversation a message belongs to, oldest first, EXCLUDING the message itself.
     *
     * The server reaches into Sent as well as the message's own folder, so every row carries the
     * folder it came from — a conversation is the one list in this app that spans folders, and
     * opening a reply means opening it where it actually lives.
     */
    async getThread(id: string): Promise<Email[]> {
        const { folder, uid } = unpackId(id);
        if (!folder || !uid) return [];
        const r = await this.get<{ folder: string; messages: SummaryJSON[] }>(
            `/thread?folder=${encodeURIComponent(folder)}&uid=${uid}`,
        );
        return (r.messages ?? [])
            .map((m) => summaryToEmail(m, m.folder || r.folder, m.uidValidity ?? 0))
            .filter((m) => m.id !== id);
    }

    async updateEmail(_accountId: string, id: string, patch: Record<string, unknown>): Promise<void> {
        const add: string[] = [];
        const remove: string[] = [];
        for (const [key, value] of Object.entries(patch)) {
            if (!key.startsWith('keywords/')) continue;
            const keyword = key.slice('keywords/'.length);
            if (value) add.push(keyword);
            else remove.push(keyword);
        }
        if (!add.length && !remove.length) return;
        await this.setFlags([id], add, remove);
        const known = this.seen.get(id);
        if (known) {
            const keywords = { ...known.keywords };
            for (const k of add) keywords[k] = true;
            for (const k of remove) delete keywords[k];
            this.seen.set(id, { ...known, keywords });
        }
    }

    async moveEmail(_accountId: string, id: string, folder: string): Promise<MoveStep[]> {
        const steps = await this.move([id], folder);
        this.seen.delete(id);
        return steps;
    }

    async destroyEmail(_accountId: string, id: string): Promise<{ erased: boolean; steps: MoveStep[] }> {
        const { erased, steps } = await this.remove([id]);
        this.seen.delete(id);
        return { erased, steps };
    }

    /** Their server has no QUOTA, so there is no storage figure to show. Saying so beats guessing. */
    async quota(_accountId?: string): Promise<{ used: number; limit: number } | null> {
        return null;
    }

    async identities(address: string): Promise<Identity[]> {
        return [await this.identity(address)];
    }

    /** The IMAP hierarchy separator. Dovecot uses a dot; a few servers use a slash. */
    delimiter = '.';

    /** Messages already fetched, so a list does not ask for what it has just been sent. */
    private seen = new Map<string, Email>();

    forget(id: string) {
        this.seen.delete(id);
    }

    // ---- the plumbing ----

    private get<T>(path: string): Promise<T> {
        return this.send<T>('GET', path);
    }

    private post<T>(path: string, body: unknown): Promise<T> {
        return this.send<T>('POST', path, {
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(body),
        });
    }

    /**
     * Attachments go up as form data rather than base64 in JSON: base64 adds a third to every
     * file, and the browser can stream a form.
     */
    private async postForm<T>(path: string, draft: OutgoingMessage, replaces?: number): Promise<T> {
        const form = new FormData();
        if (draft.fromName) form.set('fromName', draft.fromName);
        for (const field of ['to', 'cc', 'bcc'] as const) {
            for (const address of draft[field] ?? []) form.append(field, formatAddress(address));
        }
        form.set('subject', draft.subject ?? '');
        if (draft.text) form.set('text', draft.text);
        if (draft.html) form.set('html', draft.html);
        if (draft.inReplyTo) form.set('inReplyTo', draft.inReplyTo);
        if (draft.references?.length) form.set('references', draft.references.join(' '));
        if (replaces) form.set('replaces', String(replaces));
        const files = draft.attachments ?? [];
        const cids = draft.attachmentCids ?? [];
        for (let i = 0; i < files.length; i++) {
            form.append('attachments', files[i]!, files[i]!.name);
            // One cid per file, in step, so the backend can tell which is which. Empty for an
            // ordinary attachment; the field is appended either way to keep the two aligned.
            form.append('attachmentCids', cids[i] ?? '');
        }

        return this.send<T>('POST', path, { body: form });
    }

    private del<T>(path: string): Promise<T> {
        return this.send<T>('DELETE', path);
    }

    private async send<T>(method: string, path: string, init: RequestInit = {}): Promise<T> {
        let res: Response;
        try {
            const headers = new Headers(init.headers);
            if (this.mailbox) headers.set('X-Webmail-Mailbox', this.mailbox);
            res = await fetch(this.base + path, {
                ...init,
                headers,
                method,
                // The session cookie. Without this the browser leaves it behind on a
                // cross-origin development setup and every request looks signed out.
                credentials: 'same-origin',
            });
        } catch {
            throw new MailError('the server could not be reached');
        }

        // Only a client that names a mailbox: the one without is the sign-in form's, and a 401
        // there is a wrong password, not an ended session.
        if (res.status === 401 && this.mailbox) sessionEnded?.(this.mailbox);

        if (res.status === 204) return undefined as T;

        const isJSON = res.headers.get('content-type')?.includes('application/json');
        const body = isJSON ? await res.json().catch(() => null) : null;

        if (!res.ok) {
            const message = (body?.error as string) || `request failed (${res.status})`;
            throw new MailError(message, res.status, body?.reason as string | undefined);
        }
        return body as T;
    }
}

export interface OutgoingMessage {
    fromName?: string;
    to?: EmailAddress[];
    cc?: EmailAddress[];
    bcc?: EmailAddress[];
    subject?: string;
    text?: string;
    html?: string;
    inReplyTo?: string;
    references?: string[];
    attachments?: File[];
    /** One entry per attachment, naming the cid an inline image is referred to by. */
    attachmentCids?: string[];
}

function toMailbox(f: FolderJSON): Mailbox {
    return {
        id: f.name,
        name: f.display || f.name,
        parentId: f.parent || null,
        role: f.role ?? null,
        sortOrder: f.role ? (ROLE_ORDER[f.role] ?? 90) : 99,
        totalEmails: f.total,
        unreadEmails: f.unseen,
        isSubscribed: f.subscribed,
        selectable: f.selectable,
        depth: f.depth,
    };
}

/**
 * Works out what a compiled search filter actually asks for.
 *
 * A filter that is nothing but a folder is a plain listing, which is the common case and the fast
 * one. Anything else is a search. A condition the backend cannot express is thrown rather than
 * quietly dropped, so the list layer can report exactly which operator was ignored.
 */
function planQuery(
    filter: Record<string, unknown> | null,
):
    | { kind: 'list'; folder: string }
    | { kind: 'search'; query: Record<string, unknown> } {
    if (!filter) return { kind: 'list', folder: 'INBOX' };

    const conditions =
        filter.operator === 'AND' && Array.isArray(filter.conditions)
            ? (filter.conditions as Record<string, unknown>[])
            : [filter];

    if (conditions.length === 1 && typeof conditions[0]?.inMailbox === 'string' && Object.keys(conditions[0]!).length === 1) {
        return { kind: 'list', folder: conditions[0]!.inMailbox as string };
    }

    const query: Record<string, unknown> = {};
    for (const condition of conditions) {
        for (const [key, value] of Object.entries(condition)) {
            switch (key) {
                case 'inMailbox':
                    query.folder = value;
                    break;
                case 'text':
                case 'body':
                    query.text = [query.text, value].filter(Boolean).join(' ');
                    break;
                case 'from':
                    query.from = value;
                    break;
                case 'to':
                case 'cc':
                case 'bcc':
                    query.to = value;
                    break;
                case 'subject':
                    query.subject = value;
                    break;
                case 'hasAttachment':
                    query.hasAttachment = true;
                    break;
                /*
                    The four flags IMAP can actually search for. Anything else still throws, so an
                    unknown one is reported to the person rather than dropped — but "read",
                    "draft" and "answered" are no longer among the unknown ones. They were offered
                    by the search box, accepted here and then thrown away, which meant `is:read`
                    silently searched the whole folder instead.
                */
                case 'notKeyword':
                case 'hasKeyword': {
                    const flag = FLAGS[String(value).replace(/^\$/, '').toLowerCase()];
                    if (!flag) throw unsupported(key);
                    const into = key === 'hasKeyword' ? 'flags' : 'notFlags';
                    const already = Array.isArray(query[into]) ? (query[into] as string[]) : [];
                    query[into] = [...already, flag];
                    break;
                }
                case 'before':
                    query.before = String(value).slice(0, 10);
                    break;
                case 'after':
                    query.since = String(value).slice(0, 10);
                    break;
                default:
                    // attachmentName, minSize, maxSize: IMAP has no search term for any of
                    // them, and pretending otherwise would silently return the wrong mail.
                    throw unsupported(key);
            }
        }
    }
    return { kind: 'search', query };
}

function unsupported(property: string): MailError {
    return new MailError(property, undefined, undefined, 'unsupportedFilter');
}

function formatAddress(a: EmailAddress): string {
    if (!a.name) return a.email;
    // Quoted, so a comma in the name is not read as another recipient.
    return `"${a.name.replace(/"/g, '')}" <${a.email}>`;
}

/**
 * Actions arrive as a list of ids that may span folders — a search result can hold messages from
 * several — and each folder is a separate request, since IMAP works one mailbox at a time.
 */
function groupByFolder(ids: string[]): Map<string, { uids: number[]; uidValidity: number }> {
    const groups = new Map<string, { uids: number[]; uidValidity: number }>();
    for (const id of ids) {
        const { folder, uid } = unpackId(id);
        if (!folder || !uid) continue;
        const group = groups.get(folder) ?? { uids: [], uidValidity: 0 };
        group.uids.push(uid);
        groups.set(folder, group);
    }
    return groups;
}

function addresses(list: { name?: string; email: string }[] | null | undefined): EmailAddress[] | null {
    if (!list?.length) return null;
    return list.map((a) => ({ name: a.name ?? null, email: a.email }));
}

/* What the server will search for, keyed by the names the search box uses. */
const FLAGS: Record<string, string> = {
    seen: 'seen',
    flagged: 'flagged',
    draft: 'draft',
    answered: 'answered',
};

function summaryToEmail(m: SummaryJSON, folder: string, uidValidity: number): Email {
    return {
        id: packId(folder, m.uid),
        folder,
        uid: m.uid,
        uidValidity,
        size: m.size,
        receivedAt: m.date,
        sentAt: m.date,
        subject: m.subject || null,
        from: addresses(m.from),
        to: addresses(m.to),
        cc: null,
        bcc: null,
        replyTo: null,
        messageId: null,
        inReplyTo: null,
        references: null,
        preview: '',
        hasAttachment: m.hasAttachment,
        threadCount: m.threadCount,
        threadUnread: m.threadUnread,
        keywords: keywordsOf(m),
    };
}

function messageToEmail(m: MessageJSON, folder: string): Email {
    return {
        ...summaryToEmail(m, folder, m.uidValidity ?? 0),
        cc: addresses(m.cc),
        bcc: addresses(m.bcc),
        replyTo: addresses(m.replyTo),
        messageId: m.messageId ? [m.messageId] : null,
        inReplyTo: m.inReplyTo ?? null,
        references: m.references ?? null,
        listUnsubscribe: m.listUnsubscribe ?? null,
        authResults: m.authResults ?? null,
        text: m.text,
        html: m.html,
        truncated: m.truncated,
        attachments: (m.attachments ?? []).map(toPart),
        inline: (m.inline ?? []).map(toPart),
    };
}

function toPart(p: PartJSON) {
    return {
        partId: p.section,
        size: p.size,
        name: p.filename ?? null,
        type: p.mediaType,
        disposition: p.contentId ? 'inline' : 'attachment',
        cid: p.contentId ?? null,
    };
}

/*
    What the server said about a message, in the words the interface reads.

    Exported so a test can walk the whole chain: the server's JSON, this translation, and the
    helpers that ask questions of the result. The chain broke once because the two ends spelled
    the same word differently and nothing looked at both.
*/
export function keywordsOf(m: { seen?: boolean; flagged?: boolean; answered?: boolean; draft?: boolean }): Record<string, boolean> {
    const out: Record<string, boolean> = {};
    if (m.seen) out.seen = true;
    if (m.flagged) out.flagged = true;
    if (m.answered) out.answered = true;
    if (m.draft) out.draft = true;
    return out;
}

function identityKey(address: string): string {
    return `webmail.identity.${address.toLowerCase()}`;
}

function readStoredIdentity(address: string): { name: string; textSignature: string; htmlSignature: string } {
    const empty = { name: '', textSignature: '', htmlSignature: '' };
    try {
        const raw = localStorage.getItem(identityKey(address));
        if (!raw) return empty;
        return { ...empty, ...(JSON.parse(raw) as object) };
    } catch {
        return empty;
    }
}

/** One client for the whole app. */
export const client = new MailClient();
